import { Stack, Typography } from "@mui/material";
import { useCallback, useEffect } from "react";
import { useNavigate, useOutletContext } from "react-router";
import { Link, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormTextField, PrimaryButton } from "../../../../../components/form";
import { APP_COLORS } from "../../../../../enums";
import { userSignup } from "../../../../../api/auth";
import { getUserProfile } from "../../../../../api/profile";
import { useMutationWithDispatch } from "../../../../../hooks/useMutationWithDispatch";
import { ROUTE_PATHS } from "../../../../../routes/paths";
import {
  existingAmbassadorSignupSchema,
  ExistingAmbassadorSignupFormData,
  PAGE_CONFIG,
} from "./config";
import {
  handleExistingAmbassadorSignupSuccess,
  getExistingAmbassadorSignupPayload,
} from "./utils";
import {
  ExistingAmbassadorSignupResponse,
  OutletContext,
} from "./types";

function ExistingAmbassadorSignup() {
  const { setPageConfig } = useOutletContext<OutletContext>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<ExistingAmbassadorSignupFormData>({
    resolver: zodResolver(existingAmbassadorSignupSchema),
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
    },
  });

  const { mutateAsync: submitSignup, isPending: submitting } =
    useMutationWithDispatch<
      ExistingAmbassadorSignupResponse,
      {
        email: string;
        password: string;
        role: string;
      }
    >((payload) => userSignup(payload));

  const { mutateAsync: fetchUserProfile } = useMutationWithDispatch(
    getUserProfile
  );

  useEffect(() => {
    setPageConfig({ title: PAGE_CONFIG.TITLE });
    const emailFromParams = searchParams?.get("email");
    if (emailFromParams) {
      setValue("email", emailFromParams);
    }
  }, [setPageConfig, searchParams, setValue]);

  const onSubmit = useCallback(
    async (data: ExistingAmbassadorSignupFormData) => {
      try {
        const payload = getExistingAmbassadorSignupPayload(data);
        const res = await submitSignup(payload);

        if (res?.success && res?.data) {
          await handleExistingAmbassadorSignupSuccess(
            res.data,
            navigate,
            fetchUserProfile
          );
        }
      } catch (error) {
      }
    },
    [submitSignup, navigate, fetchUserProfile]
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Stack>
        <FormTextField
          name="email"
          control={control}
          label="Email"
          required
          error={errors.email?.message}
          textFieldProps={{ type: "email", disabled: true }}
        />

        <FormTextField
          name="password"
          control={control}
          label="Password"
          required
          error={errors.password?.message}
          textFieldProps={{ type: "password" }}
        />

        <FormTextField
          name="confirmPassword"
          control={control}
          label="Repeat Password"
          required
          error={errors.confirmPassword?.message}
          textFieldProps={{ type: "password" }}
        />

        <Stack gap={2} my={2}>
          <PrimaryButton loading={submitting}>Create an account</PrimaryButton>
          <Typography variant="medium" sx={{ textAlign: "center" }}>
            Have an account,
            <Link
              to={ROUTE_PATHS.AUTH.LOGIN}
              style={{ textDecoration: "none" }}
            >
              <Typography
                component="span"
                sx={{ ml: 1, cursor: "pointer", display: "inline" }}
                variant="body-ibmPlexMono"
                color={APP_COLORS.secondary}
              >
                Sign In
              </Typography>
            </Link>
          </Typography>
        </Stack>
      </Stack>
    </form>
  );
}

export default ExistingAmbassadorSignup;
